import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TemplateManagementService } from '../../../../services/templates/template.management.service';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import TemplatePayload from '../../../../models/templates/template.payload';
import TagModel from '../../../../models/templates/tag.model';
import { TemplateType } from '../../../../models/templates/template.type';
import FormElementDataModel from '../../../../models/form.element.data.model';
import { TEMPLATE_RESTRICTION_TYPES } from '../../../../constants';
import { AdminTemplatesStateService } from '../../../../routes/admin/admin-templates/admin-templates-state.service';
import { AcknowledgementSettingsComponent } from '../acknowledgement-settings/acknowledgement-settings.component';
import { TemplateAssignmentType } from '../../../../models/templates/template.assignment.type';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss', '../tool-settings.scss']
})
export class TagsComponent implements OnChanges {
  @Input() public tms: TemplateManagementService;
  @Input() public templatePayload: TemplatePayload;
  @Input() public strictReadOnly: boolean = false;
  @Input() public displayAcknowledgementSection: boolean = false;
  @ViewChild(AcknowledgementSettingsComponent) ackSettingsComponent: AcknowledgementSettingsComponent;

  public tagsForm: FormGroup = this.formBuilder.group({
    tagName: ['', [Validators.maxLength(25)]],
    name: ['', [Validators.required, Validators.maxLength(25)]],
    description: ['', [Validators.maxLength(250)]],
    templateType: [TemplateType.Layout, [Validators.required]]
  });

  public ackDefaultTemplate: boolean = false;
  public ackEmailSubject: string = '';
  public ackSenderEmail: string = '';
  public ackSenderName: string = '';

  @Output() invalidTagFormEmitter: EventEmitter<void> = new EventEmitter<void>();
  public existingTags: TagModel[] = [];
  public templateTypeOptions: FormElementDataModel[] = TEMPLATE_RESTRICTION_TYPES;

  public get isButtonDisabled(): boolean {
    return this.adminTemplatesStateService.isSaveUpdateButtonDisabled;
  }

  constructor(
    private formBuilder: FormBuilder,
    private adminTemplatesStateService: AdminTemplatesStateService,
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.templatePayload && changes.templatePayload.currentValue) {
      this.tagsForm.get('name').setValue(this.templatePayload.name);
      this.tagsForm.get('description').setValue(this.templatePayload.description);
      this.tagsForm.get('templateType').setValue(this.templatePayload.templateType);
      this.existingTags = this.templatePayload.tags;

      this.ackDefaultTemplate = this.templatePayload.ackDefaultTemplate || false;
      this.ackEmailSubject = this.templatePayload.ackEmailSubject || '';
      this.ackSenderEmail = this.templatePayload.ackSenderEmail || '';
      this.ackSenderName = this.templatePayload.ackSenderName || '';
    }
  }

  public updateTemplate(): void {
    const emailControl = this.ackSettingsComponent?.ackForm.get('senderEmail');

    this.tagsForm.markAllAsTouched();
    this.ackSettingsComponent?.markAllAsTouched();

    // Check if we have an email control and it's invalid
    if (emailControl && emailControl.value && emailControl.invalid) {
      this.invalidTagFormEmitter.emit();
      return;
    }

    if (this.displayAcknowledgementSection && this.tms) {
      const isEmailSubjectRequired = (this.tms.templateAssignmentType === TemplateAssignmentType.Campaign || !this.tms.isLetterBuilder) && this.ackDefaultTemplate;
      if (isEmailSubjectRequired && (!this.ackEmailSubject || this.ackEmailSubject.trim() === '')) {
        this.invalidTagFormEmitter.emit();
        return;
      }
    }

    if (this.tagsForm.invalid) {
      this.invalidTagFormEmitter.emit();
      return;
    }

    if (this.tagsForm.get('tagName').value && this.tagsForm.get('tagName').value.toString()) {
      this.onEnterPressed(null);
    }

    // Final validation check
    if (emailControl && emailControl.value && emailControl.invalid) {

      return;
    }

    // Check if acknowledgement form is valid when required
    if (this.displayAcknowledgementSection && this.ackSettingsComponent?.ackForm.invalid) {

      this.invalidTagFormEmitter.emit();
      return;
    }

    const payload: TemplatePayload = {
      tags: this.existingTags,
      name: this.tagsForm.value.name,
      description: this.tagsForm.value.description,
      templateType: this.tagsForm.value.templateType,
      ackDefaultTemplate: this.ackDefaultTemplate,
      ackEmailSubject: this.ackEmailSubject,
      ackSenderEmail: this.ackSenderEmail,
      ackSenderName: this.ackSenderName
    };

    this.tms.triggerSaveTemplate(payload);
  }

  public removeTag(index: number): void {
    if (this.strictReadOnly) {
      return;
    }
    this.existingTags.splice(index, 1);
  }

  public onEnterPressed(event: KeyboardEvent): void {
    const value: string = this.tagsForm.get('tagName').value.toString();
    if (!value || this.tagsForm.get('tagName').invalid) {
      return;
    }
    this.existingTags.push({name: value});
    this.tagsForm.get('tagName').reset();
  }

  onAcknowledgementChange(details: any): void {
    if (this.displayAcknowledgementSection && details.ackSenderEmail && this.ackSettingsComponent?.ackForm.get('senderEmail').invalid) {
      this.invalidTagFormEmitter.emit();
      return;
    }
    
    this.ackDefaultTemplate = details.ackDefaultTemplate;
    this.ackEmailSubject = details.ackEmailSubject;
    this.ackSenderEmail = details.ackSenderEmail;
    this.ackSenderName = details.ackSenderName;
  }
}
